import React, { Component } from "react";
import ReactGA from "react-ga";
import BannerImg from "../../assets/images/banner/hajj-and-umrah-slider.jpg";
import HajjUmrahBooking from "../../assets/images/umrah-solution-overview.png";
import UmrahOneBox from "../../assets/images/umrah-one-box.png";
import umrahsolution from "../../assets/images/umrahsolution.png";
import ProductHeritagePage from "../../components/product/product-heritage-page";

const page = {
  title: "Umrah Booking Solution Heritage, Travel Operations Platform | Avani Cimcon",
  description:
    "Umrah Booking Solution shows Avani Cimcon's travel-platform heritage: Maqam GDS integration, BRN generation, B2B and B2C workflows, multilingual booking and back-office automation.",
  keywords:
    "Umrah Booking Solutions, Umrah Booking Solution, Umrah solution, Maqam GDS, BRN generation, B2C Umrah booking, B2B Umrah booking, travel platform, travel booking solution, travel back office, e-wallet, reservation, accounting, multilingual booking, enterprise software engineering, legacy modernization, AI enablement",
  canonical: "https://www.umrahsolutions.com",
  ogImage: "https://www.avanicimcon.com/images/banner/hajj-and-umrah-slider.jpg",
  noindex: true,
  hero: {
    eyebrow: "Product Heritage",
    heading: "Umrah Booking Solution",
    lead:
      "This page is about a travel platform engineered for complex Umrah operations, not about selling a standalone product in isolation.",
    image: BannerImg,
    tags: ["Maqam GDS", "BRN generation", "B2C / B2B", "Back-office automation"],
    panelKicker: "Platform depth in a regulated travel workflow.",
    panelTitle: "A travel operations platform built for booking, visa and fulfillment complexity.",
    panelText:
      "The solution handled itinerary creation, reservations, e-wallet logic and back-office automation while keeping the B2C and B2B experiences aligned.",
    stats: [
      { value: "Maqam", label: "Official travel ecosystem integration" },
      { value: "BRN", label: "Booking reference number automation" },
      { value: "Multilingual", label: "Arabic and English workflows" },
    ],
    primaryCta: { label: "Contact Us", href: "/contact-us" },
    secondaryCta: { label: "Explore Travel & Hospitality", href: "/travel-hospitality" },
    externalLinks: [{ label: "Maqam GDS", href: "https://maqam.gds.haj.gov.sa/" }],
  },
  overview: {
    eyebrow: "Platform Overview",
    title: "A travel operations system designed around Umrah booking workflows",
    lead:
      "The page should demonstrate the business logic, integration complexity and operational requirements behind the platform.",
    paragraphs: [
      "UmrahOne is an off-the-shelf Umrah Booking Solution that allows B2C clients and B2B agents to book packages, flights, hotels, transportation and ground services online.",
      "The platform is tied to automatic BRNs from Maqam GDS and e-visa generation, which makes it an excellent proof page for enterprise workflow engineering in a regulated travel context.",
    ],
    callout: {
      title: "What the page should prove",
      text: "The solution is evidence of domain-specific engineering for a complex travel ecosystem with real operational dependencies.",
      items: [
        "Travel booking orchestration",
        "Visa and BRN workflow integration",
        "Operational automation across B2C and B2B",
      ],
    },
  },
  businessProblem: {
    eyebrow: "Business Problem Solved",
    title: "Umrah travel required a system that could coordinate bookings, visa logic and back-office operations",
    lead:
      "The platform solved a very specific enterprise problem: how to connect official travel ecosystem rules with a usable booking experience.",
    items: [
      {
        title: "Complex booking conditions",
        text: "The platform had to manage hotels, flights, transportation and ground services as part of one flow.",
      },
      {
        title: "Official integration dependencies",
        text: "Maqam GDS, BRN generation and e-visa flows were not optional extras; they were core to the workflow.",
      },
      {
        title: "Agent and customer separation",
        text: "The system had to serve both B2C and B2B users without losing control of the process.",
      },
    ],
  },
  architecture: {
    eyebrow: "Enterprise Architecture",
    title: "A layered travel platform with booking, visa and operational subsystems",
    lead:
      "The architecture story matters because it explains how the system handled multiple business layers at once.",
    paragraphs: [
      "The platform supported automatic BRN generation through Maqam GDS, e-wallet logic, multi-step reservation flows and back-office automation, all of which required careful orchestration.",
      "That makes the page a real proof of architecture and integration experience in a travel ecosystem that has high operational and compliance demands.",
    ],
    callout: {
      title: "Architecture highlights",
      items: [
        "B2C and B2B user flows",
        "BRN and visa ecosystem integration",
        "Back-office and accounting support",
        "Multilingual user experience",
      ],
    },
  },
  capabilities: {
    eyebrow: "Key Platform Capabilities",
    title: "The solution was built around travel operations, not generic booking features",
    lead:
      "These capabilities preserve the original product detail while reframing it as platform evidence.",
    items: [
      {
        title: "B2C booking flow",
        text: "Clients could book packages, flights, hotels, transportation and ground services online.",
      },
      {
        title: "B2B agent flow",
        text: "Agents had e-wallet statements, reconciliation and online reservation cancellation facilities.",
      },
      {
        title: "Visa process support",
        text: "The workflow included BRN generation and visa-status related operations.",
      },
      {
        title: "Multilingual delivery",
        text: "Arabic and English support helped the platform serve a wider user base.",
      },
    ],
  },
  challenges: {
    eyebrow: "Engineering Challenges Solved",
    title: "The platform handled the real complexity of regulated travel operations",
    lead:
      "That operational complexity is exactly why this page belongs in Pack 04.",
    items: [
      {
        title: "Stateful booking operations",
        text: "Bookings had to remain consistent across reservations, payments, cancellations and back-office actions.",
      },
      {
        title: "Official dependency management",
        text: "The travel ecosystem required clean integration with external systems and rules.",
      },
      {
        title: "User-role complexity",
        text: "The same platform had to serve individual travelers, agents and operational teams.",
      },
    ],
  },
  scalability: {
    eyebrow: "Scalability & Operational Considerations",
    title: "The platform was built to handle volume, multilingual use and back-office scale",
    lead:
      "The original feature set already shows a serious operations mindset.",
    paragraphs: [
      "Bulk bookings for hotel rooms, multilingual notifications and mobile-responsive interfaces show that the platform was designed for real-world scale rather than demo-only usage.",
      "Back-office and accounting integration also show that the engineering extended beyond the booking front end into operational control.",
    ],
    callout: {
      title: "Operational lessons",
      items: [
        "Support multi-user and multi-role operations",
        "Keep booking and back-office systems in sync",
        "Design for multilingual and mobile usage",
      ],
    },
  },
  media: {
    eyebrow: "Platform Imagery",
    title: "The screenshots are proof, so they stay in the story",
    lead:
      "These images show the booking and operations context behind the product.",
    items: [
      {
        src: HajjUmrahBooking,
        alt: "Umrah booking solution overview diagram",
        caption: "Solution overview",
        text: "The overview image reinforces the platform scope and the travel-operation focus.",
      },
      {
        src: UmrahOneBox,
        alt: "UmrahOne booking platform feature image",
        caption: "UmrahOne experience",
        text: "The product image helps preserve the legacy visual language already associated with the page.",
      },
      {
        src: umrahsolution,
        alt: "Umrah booking solution screenshot",
        caption: "Platform screenshot",
        text: "The screenshot shows the product as a real operational system rather than a marketing mockup.",
      },
    ],
  },
  integration: {
    eyebrow: "Technology & Integration Experience",
    title: "Maqam GDS, e-wallets and back-office flows made this a genuine integration project",
    lead:
      "That integration depth is what makes the page valuable as enterprise proof.",
    paragraphs: [
      "The platform connected to Maqam GDS for automatic BRNs, used e-wallet credentials and supported operational workflows that spanned booking and fulfillment.",
      "Those details preserve the historical terminology while showing that the product is proof of difficult integration work.",
    ],
    callout: {
      title: "Integration capabilities",
      items: [
        "Maqam GDS linkage",
        "E-wallet and account handling",
        "Reservation cancellation flows",
        "Ground-services orchestration",
      ],
    },
  },
  benefits: {
    eyebrow: "How This Experience Benefits Our Clients",
    title: "The Umrah platform proves Avani Cimcon can handle travel complexity with discipline",
    lead:
      "That translates directly into client value for modernization and AI adoption.",
    items: [
      {
        title: "Domain realism",
        text: "We know what it means to build software around real travel ecosystem dependencies.",
      },
      {
        title: "Operational depth",
        text: "The platform covers booking, account, visa and back-office concerns together.",
      },
      {
        title: "Product ownership",
        text: "The page shows Avani Cimcon has owned and evolved a platform over time.",
      },
      {
        title: "Enterprise trust",
        text: "The complexity of the workflow reinforces credibility with enterprise buyers.",
      },
    ],
  },
  modernization: {
    eyebrow: "Legacy Modernization Opportunities",
    title: "The same travel platform lessons apply to modern client systems",
    lead:
      "This page should show how operational travel platforms evolve without losing utility.",
    paragraphs: [
      "The Umrah solution demonstrates how booking, identity, payment and back-office systems can be coordinated in one platform, which is useful experience for clients modernizing travel or regulated workflow systems.",
      "That makes the page a strong bridge between product heritage and the enterprise modernization story on the rest of the site.",
    ],
    callout: {
      title: "Modernization priorities",
      items: [
        "Preserve the operational workflow core",
        "Modernize booking and user experience layers first",
        "Keep external integration contracts stable",
        "Reduce manual back-office handling over time",
      ],
    },
  },
  ai: {
    eyebrow: "AI Enablement Opportunities",
    title: "AI can improve travel operations once the platform and data flows are stable",
    lead:
      "AI should enhance booking and operational support, not replace the platform story.",
    items: [
      {
        title: "Booking assistance",
        text: "Use AI to help users choose packages, answer booking questions and reduce friction.",
      },
      {
        title: "Operational support",
        text: "Use AI to assist with reconciliation, exception handling and customer support.",
      },
      {
        title: "Workflow intelligence",
        text: "Use AI to analyze booking patterns, peak demand and support issues across the platform.",
      },
    ],
  },
  relatedIndustries: {
    eyebrow: "Related Industries",
    title: "The travel platform story connects naturally to the vertical pages",
    lead:
      "These links strengthen the relationship between platform heritage and industry expertise.",
    items: [
      { label: "Travel & Hospitality", href: "/travel-hospitality", text: "Broader travel workflow expertise." },
      { label: "Insurance", href: "/insurance", text: "Complex regulated workflow operations." },
      { label: "Healthcare", href: "/healthcare", text: "Operational and integration discipline." },
      { label: "Legal", href: "/legal", text: "Document and transaction workflows." },
    ],
  },
  relatedLegacyServices: {
    eyebrow: "Related Legacy Services",
    title: "The platform fits into the rest of the enterprise engineering portfolio",
    lead:
      "These links help the page behave like proof, not a standalone product ad.",
    items: [
      { label: "DXchange", href: "/dxchange", text: "Integration platform heritage." },
      { label: "CMS", href: "/cms", text: "Portal and booking front-end heritage." },
      { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Rules and policy logic." },
      { label: "IT Consulting Services", href: "/it-consulting-services", text: "Modernization and platform strategy." },
    ],
  },
  why: {
    eyebrow: "Why Avani Cimcon",
    title: "The Umrah platform shows the company can build and evolve domain-specific software",
    lead:
      "That is the exact kind of proof enterprise buyers need.",
    items: [
      {
        title: "Travel domain expertise",
        text: "The platform reflects deep understanding of travel operations and official dependencies.",
      },
      {
        title: "Platform ownership",
        text: "The solution is evidence of long-term engineering and product maintenance.",
      },
      {
        title: "Complex workflow handling",
        text: "B2C, B2B and back-office flows were all part of the same system.",
      },
      {
        title: "Search value preserved",
        text: "The page keeps the original terms such as Umrah Booking Solution and Maqam GDS.",
      },
    ],
  },
  cta: {
    eyebrow: "Talk to Us",
    title: "Use this travel-platform experience to support your own modernization program",
    text:
      "If you need travel or regulated workflow software, we can bring this same level of discipline to your project.",
    primary: { label: "Contact Us", href: "/contact-us" },
    secondary: { label: "Explore Travel & Hospitality", href: "/travel-hospitality" },
  },
};

class UmrahBookingSolution extends Component {
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    return <ProductHeritagePage {...page} />;
  }
}

export default UmrahBookingSolution;
